<template>
  <div class="container-fluid py-3">
    <!-- Page header -->
    <div class="d-flex align-items-center gap-2 mb-3">
      <h5 class="mb-0">Target vs Achievement — Detail View</h5>
    </div>

    <!-- Filters card -->
    <div class="card mb-3">
      <div class="card-body">
        <div class="row g-2">
          <div v-if="!isDistributor" class="col-sm-6 col-md-2">
            <label class="form-label small text-muted mb-1">Role</label>
            <select
              v-model="selectedRole"
              class="form-select form-select-sm"
              @change="handleRoleChange"
            >
              <option value="">All Roles</option>
              <option v-for="o in orgStructures" :key="o.id" :value="o.id">
                {{ o.description }}
              </option>
            </select>
          </div>

          <div
            v-if="isDistributor || selectedRole !== ''"
            class="col-sm-6 col-md-2"
          >
            <label class="form-label small text-muted mb-1">User</label>
            <select
              v-model="selectedName"
              class="form-select form-select-sm"
              :disabled="isDistributor"
            >
              <option v-if="!isDistributor" :value="null">
                {{
                  isDistributorRoleSelected
                    ? "Select Distributor"
                    : userSelectPlaceholder
                }}
              </option>

              <option
                v-if="!isDistributor && isDistributorRoleSelected"
                value=""
              >
                All Distributors
              </option>

              <option v-if="isDistributor" :value="profileId">
                {{ loggedUserName }}
              </option>

              <option
                v-for="u in usersInOrgStructList"
                :key="u.profile_id"
                :value="u.profile_id"
              >
                {{ u.name }}
              </option>
              <option v-if="isDistributor" :value="profileId">
                {{ loggedUserName }}
              </option>
              <option
                v-for="u in usersInOrgStructList"
                :key="u.profile_id"
                :value="u.profile_id"
              >
                {{ u.name }}
              </option>
            </select>
          </div>

          <div
            v-if="isDistributor || selectedName !== ''"
            class="col-sm-6 col-md-2"
          >
            <label class="form-label small text-muted mb-1">Vehicle</label>
            <select
              v-model="selectedVehicle"
              class="form-select form-select-sm"
            >
              <option value="">All Vehicles</option>
              <option
                v-for="lorry in vehicles"
                :key="lorry.id"
                :value="lorry.id"
              >
                {{ lorry.vehicle_no }}
              </option>
            </select>
          </div>

          <div class="col-sm-6 col-md-2">
            <label class="form-label small text-muted mb-1">Year</label>
            <select v-model="selectedYear" class="form-select form-select-sm">
              <option v-for="y in yearOptions" :key="y" :value="y">
                {{ y }}
              </option>
            </select>
          </div>

          <div class="col-sm-6 col-md-2">
            <label class="form-label small text-muted mb-1">Month</label>
            <select v-model="selectedMonth" class="form-select form-select-sm">
              <option value="">All Year</option>
              <option v-for="(m, i) in MONTHS" :key="i" :value="i + 1">
                {{ m }}
              </option>
            </select>
          </div>

          <div class="col-sm-6 col-md-2">
            <label class="form-label small text-muted mb-1">SKU</label>
            <select v-model="selectedSku" class="form-select form-select-sm">
              <option value="">All SKUs</option>
              <option
                v-for="item in items"
                :key="item.variant_id"
                :value="item.variant_id"
              >
                {{ item.description }}
              </option>
            </select>
          </div>

          <div class="col-sm-6 col-md-2">
            <label class="form-label small text-muted mb-1">Measure</label>
            <select
              v-model="selectedMeasure"
              class="form-select form-select-sm"
            >
              <option
                value="mt"
                :disabled="!selectedSkuAllowedMeasures.includes('mt')"
              >
                Volume (MT)
              </option>

              <option
                value="qty"
                :disabled="!selectedSkuAllowedMeasures.includes('qty')"
              >
                Quantity (Qty)
              </option>
            </select>
          </div>

          <div class="col-sm-6 col-md-2 d-flex align-items-end">
            <button
              class="btn btn-sm btn-outline-secondary w-100"
              @click="exportExcel"
            >
              <i class="fa fa-download me-1"></i> Export Excel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="d-flex justify-content-center py-5">
      <div class="spinner-border text-primary"></div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-5 text-muted">{{ error }}</div>

    <template v-else-if="tableRows.length > 0">
      <!-- Summary metric cards -->
      <div class="row g-2 mb-3">
        <div class="col-6 col-md-4">
          <div class="card text-center">
            <div class="card-body py-3">
              <div class="text-muted small mb-1">Total Target</div>
              <div class="fs-5 fw-semibold">
                <!-- {{ Math.round(summary.target) }} -->
                {{ formatValue(summary.target) }}
              </div>
              <div class="text-muted" style="font-size: 11px">
                {{ measureLabel }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-4">
          <div class="card text-center">
            <div class="card-body py-3">
              <div class="text-muted small mb-1">Total Achievement</div>
              <div class="fs-5 fw-semibold">
                {{ formatValue(summary.achieved) }}
              </div>
              <div class="text-muted" style="font-size: 11px">
                {{ measureLabel }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-4">
          <div class="card text-center">
            <div class="card-body py-3">
              <div class="text-muted small mb-1">Achievement %</div>
              <div
                class="fs-5 fw-semibold"
                :class="summary.pct >= 100 ? 'text-success' : 'text-danger'"
              >
                {{ summary.pct.toFixed(1) }}%
              </div>
              <div class="text-muted" style="font-size: 11px">
                Variance: {{ formatVariance(summary.variance) }}
                {{ measureLabel }}
              </div>
            </div>
          </div>
        </div>
        <!-- <div class="col-6 col-md-3">
          <div class="card text-center">
            <div class="card-body py-3">
              <div class="text-muted small mb-1">{{ selectedMonth ? 'Days with data' : 'Months with data' }}</div>
              <div class="fs-5 fw-semibold">{{ summary.rowsWithData }} / {{ tableRows.length }}</div>
              <div class="text-muted" style="font-size:11px">{{ selectedMonth ? 'days recorded' : 'months recorded' }}</div>
            </div>
          </div>
        </div> -->
      </div>

      <!-- Data table -->
      <div class="card">
        <div
          class="card-header d-flex justify-content-between align-items-center"
        >
          <span class="fw-semibold">
            {{
              selectedMonth
                ? `Daily Breakdown — ${
                    MONTHS[selectedMonth - 1]
                  } ${selectedYear}`
                : `Monthly Summary — ${selectedYear}`
            }}
          </span>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover table-sm mb-0">
              <thead class="table-light">
                <tr>
                  <th>#</th>
                  <th>{{ selectedMonth ? "Date" : "Month" }}</th>
                  <th class="text-end">
                    {{ selectedMonth ? "Cum. Target (MT)" : "Target (MT)" }}
                  </th>
                  <th class="text-end">
                    {{ selectedMonth ? "Cum. Target (Qty)" : "Target (Qty)" }}
                  </th>
                  <th class="text-end">
                    {{
                      selectedMonth
                        ? "Cum. Achievement (MT)"
                        : "Achievement (MT)"
                    }}
                  </th>
                  <th class="text-end">
                    {{
                      selectedMonth
                        ? "Cum. Achievement (Qty)"
                        : "Achievement (Qty)"
                    }}
                  </th>
                  <th class="text-end">Ach %</th>
                  <th class="text-end">Variance ({{ measureLabel }})</th>
                  <th style="width: 120px">Progress</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in tableRows"
                  :key="i"
                  :class="
                    selectedMonth && row.isWorkingDay === false
                      ? 'extra-sale-day'
                      : ''
                  "
                >
                  <td class="text-muted">{{ i + 1 }}</td>
                  <td>{{ row.label }}</td>
                  <!-- <td class="text-end">{{ row.target_mt.toFixed(4) }}</td> -->
                  <td class="text-end">{{ formatMTValue(row.target_mt) }}</td>
                  <td class="text-end">
                    {{
                      selectedMonth
                        ? row.target_qty.toFixed(2)
                        : Math.round(row.target_qty).toLocaleString()
                    }}
                  </td>
                  <!-- <td class="text-end">{{ row.achieved_mt.toFixed(4) }}</td> -->
                  <td class="text-end">{{ formatMTValue(row.achieved_mt) }}</td>
                  <td class="text-end">
                    {{
                      selectedMonth
                        ? row.achieved_qty.toFixed(2)
                        : Math.round(row.achieved_qty).toLocaleString()
                    }}
                  </td>
                  <td class="text-end">
                    <span
                      :class="
                        row.pct >= 100 ? 'badge bg-success' : 'badge bg-danger'
                      "
                    >
                      {{ row.pct.toFixed(1) }}%
                    </span>
                  </td>
                  <td
                    class="text-end"
                    :class="row.variance >= 0 ? 'text-success' : 'text-danger'"
                  >
                    {{ formatVariance(row.variance) }}
                  </td>
                  <td>
                    <div class="progress" style="height: 6px">
                      <div
                        class="progress-bar"
                        :class="
                          row.pct >= 100
                            ? 'bg-success'
                            : row.pct >= 75
                            ? 'bg-warning'
                            : 'bg-danger'
                        "
                        :style="{ width: Math.min(row.pct, 100) + '%' }"
                      ></div>
                    </div>
                  </td>
                </tr>
              </tbody>
              <!-- Totals footer -->
              <tfoot class="table-light fw-semibold">
                <tr>
                  <td colspan="2">Total</td>
                  <!-- <td class="text-end">{{ formatValue(summary.target_mt) }}</td> -->
                  <td class="text-end">
                    {{ formatMTValue(summary.target_mt) }}
                  </td>
                  <td class="text-end">
                    {{
                      selectedMonth
                        ? summary.target_qty.toFixed(2)
                        : summary.target_qty
                    }}
                  </td>
                  <td class="text-end">
                    <!-- {{ formatValue(summary.achieved_mt) }} -->
                    {{ formatMTValue(summary.achieved_mt) }}
                  </td>
                  <td class="text-end">
                    {{
                      selectedMonth
                        ? summary.achieved_qty.toFixed(2)
                        : summary.achieved_qty
                    }}
                  </td>
                  <td class="text-end">{{ summary.pct.toFixed(1) }}%</td>
                  <td
                    class="text-end"
                    :class="
                      summary.variance >= 0 ? 'text-success' : 'text-danger'
                    "
                  >
                    {{ formatVariance(summary.variance) }}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import {
  fetchNextOrgStructList,
  fetchUserListByRole,
  fetchTargetsVsAchievements,
  fetchItemNameList,
  fetchDistributorVehicles,
} from "@/services/dataService";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default {
  name: "TargetAchievementDetail",

  data() {
    const q = this.$route?.query || {};
    return {
      MONTHS,
      localToken: localStorage.getItem("token_set"),
      profileId: localStorage.getItem("profile_id"),
      isDistributor: localStorage.getItem("user_label") === "DISTRIBUTOR",
      loggedUserName: localStorage.getItem("business_name"),

      loading: false,
      error: null,

      orgStructures: [],
      usersInOrgStructList: [],
      items: [],
      vehicles: [],

      // Pre-fill from query params passed by the chart page
      selectedRole: q.role || "",
      selectedName: q.name ?? null,
      selectedYear: Number(q.year) || new Date().getFullYear(),
      selectedMonth: Number(q.month) || "",
      selectedSku: q.sku || "",
      selectedMeasure: q.measure || "mt",
      selectedVehicle: "",

      rawData: [],
    };
  },

  mounted() {
    if (this.isDistributor) {
      this.selectedName = this.profileId;
      this.fetchVehicles(this.profileId);
    } else {
      this.fetchOrgStructList();
    }

    this.loadItems();
    if (this.selectedRole) this.fetchUsersInOrgStructure();
    if (this.selectedName) this.fetchVehicles(this.selectedName);
    this.fetchData();
  },

  computed: {
    isDistributorRoleSelected() {
      const selected = this.orgStructures.find(
        (org) => org.id === this.selectedRole,
      );

      return selected?.description?.toUpperCase() === "DISTRIBUTOR";
    },

    userSelectPlaceholder() {
      if (!this.selectedRole) return "Select User";

      const selected = this.orgStructures.find(
        (org) => org.id === this.selectedRole,
      );
      return selected ? `Select ${selected.description}` : "Select User";
    },

    measureLabel() {
      return this.selectedMeasure === "mt" ? "MT" : "Qty";
    },

    yearOptions() {
      const years = [];
      for (let y = new Date().getFullYear(); y >= 2000; y--) years.push(y);
      return years;
    },

    workingDaysCount() {
      if (!this.selectedMonth || !this.rawData.length) return 0;
      const monthData = this.rawData.find(
        (d) => d.month === Number(this.selectedMonth),
      );
      return monthData ? (monthData.working_days || []).length : 0;
    },

    selectedSkuAllowedMeasures() {
      const selectedItem = this.items.find(
        (item) => item.variant_id == this.selectedSku,
      );

      return selectedItem?.allowed_measures || "mt,qty";
    },

    tableRows() {
      if (!this.rawData.length) return [];
      const isMT = this.selectedMeasure === "mt";

      if (this.selectedMonth) {
        const monthData = this.rawData.find(
          (d) => d.month === Number(this.selectedMonth),
        );
        console.log("monthData:", monthData);
        if (!monthData) return [];

        console.log("working_days:", monthData.working_days);

        const workingDaySet = new Set(
          (monthData.working_days || []).map(Number),
        );

        const extraDays = (monthData.daily_achievements || [])
          .map((d) => Number(d.day))
          .filter((day) => !workingDaySet.has(day));

        const allDays = [...workingDaySet, ...extraDays].sort((a, b) => a - b);
        if (!allDays.length) return [];

        const totalWorkingDays = workingDaySet.size;
        const totalTargetMt = monthData.target_mt ?? 0;
        const totalTargetQty = monthData.target_qty ?? 0;
        const perDayMt = totalTargetMt / totalWorkingDays;
        const perDayQty = totalTargetQty / totalWorkingDays;

        const achMap = {};
        (monthData.daily_achievements || []).forEach((d) => {
          achMap[Number(d.day)] = {
            mt: d.achieved_mt ?? 0,
            qty: d.achieved_qty ?? 0,
          };
        });

        let cumTargetMt = 0;
        let cumTargetQty = 0;
        let cumAchMt = 0;
        let cumAchQty = 0;

        return allDays.map((day) => {
          const isWorkingDay = workingDaySet.has(day);
          if (isWorkingDay) {
            cumTargetMt += perDayMt;
            cumTargetQty += perDayQty;
          }

          const ach = achMap[day] || { mt: 0, qty: 0 };
          cumAchMt += ach.mt;
          cumAchQty += ach.qty;

          const cumTarget = isMT ? cumTargetMt : cumTargetQty;
          const cumAchieved = isMT ? cumAchMt : cumAchQty;
          const variance = cumAchieved - cumTarget;
          const pct = cumTarget > 0 ? (cumAchieved / cumTarget) * 100 : 0;
          const label = `${MONTHS[this.selectedMonth - 1]} ${String(
            day,
          ).padStart(2, "0")}`;

          return {
            label,
            target_mt: cumTargetMt,
            target_qty: cumTargetQty,
            achieved_mt: cumAchMt,
            achieved_qty: cumAchQty,
            variance,
            pct,
            isWorkingDay,
          };
        });
      } else {
        // All-year view: one row per month, aggregate from top-level month fields
        const byMonth = Array.from({ length: 12 }, (_, i) => ({
          label: MONTHS[i],
          target_mt: 0,
          achieved_mt: 0,
          target_qty: 0,
          achieved_qty: 0,
        }));
        this.rawData.forEach((d) => {
          const idx = (d.month ?? 1) - 1;
          if (idx >= 0 && idx < 12) {
            byMonth[idx].target_mt += d.target_mt ?? 0;
            byMonth[idx].achieved_mt += d.achieved_mt ?? 0;
            byMonth[idx].target_qty += d.target_qty ?? 0;
            byMonth[idx].achieved_qty += d.achieved_qty ?? 0;
          }
        });
        return byMonth.map((r) => {
          const target =
            this.selectedMeasure === "mt" ? r.target_mt : r.target_qty;
          const achieved =
            this.selectedMeasure === "mt" ? r.achieved_mt : r.achieved_qty;
          const variance = achieved - target;
          const pct = target > 0 ? (achieved / target) * 100 : 0;
          return { ...r, variance, pct };
        });
      }
    },

    summary() {
      if (!this.tableRows.length) {
        return {
          target: 0,
          achieved: 0,
          variance: 0,
          pct: 0,
          target_mt: 0,
          achieved_mt: 0,
          target_qty: 0,
          achieved_qty: 0,
        };
      }

      if (this.selectedMonth) {
        const last = this.tableRows[this.tableRows.length - 1];
        const target =
          this.selectedMeasure === "mt" ? last.target_mt : last.target_qty;
        const achieved =
          this.selectedMeasure === "mt" ? last.achieved_mt : last.achieved_qty;
        const variance = achieved - target;
        const pct = target > 0 ? (achieved / target) * 100 : 0;
        return {
          target,
          achieved,
          variance,
          pct,
          target_mt: last.target_mt,
          achieved_mt: last.achieved_mt,
          target_qty: last.target_qty,
          achieved_qty: last.achieved_qty,
        };
      }

      // All-year
      const target_mt = this.tableRows.reduce((s, r) => s + r.target_mt, 0);
      const achieved_mt = this.tableRows.reduce((s, r) => s + r.achieved_mt, 0);
      const target_qty = this.tableRows.reduce((s, r) => s + r.target_qty, 0);
      const achieved_qty = this.tableRows.reduce(
        (s, r) => s + r.achieved_qty,
        0,
      );
      const target = this.selectedMeasure === "mt" ? target_mt : target_qty;
      const achieved =
        this.selectedMeasure === "mt" ? achieved_mt : achieved_qty;
      const variance = achieved - target;
      const pct = target > 0 ? (achieved / target) * 100 : 0;
      return {
        target,
        achieved,
        variance,
        pct,
        target_mt,
        achieved_mt,
        target_qty,
        achieved_qty,
      };
    },
  },

  watch: {
    selectedName(v) {
      this.vehicles = [];
      this.selectedVehicle = "";

      if (v) {
        this.fetchVehicles(v);
      }

      this.fetchData();
    },
    selectedYear() {
      this.fetchData();
    },
    // selectedSku() {
    //   this.fetchData();
    // },
    selectedSku(v) {
      const selectedItem = this.items.find((item) => item.variant_id == v);

      if (selectedItem?.allowed_measures === "qty") {
        this.selectedMeasure = "qty";
      }

      this.fetchData();
    },
    selectedVehicle() {
      this.fetchData();
    },
  },

  methods: {
    // formatValue(v) {
    //   return this.selectedMeasure === "mt" ? v.toFixed(2) : v.toLocaleString();
    // },

    // formatVariance(v) {
    //   const str = this.formatValue(Math.abs(v));
    //   return (v >= 0 ? "+" : "−") + str;
    // },

    truncateToDecimals(value, decimals = 2) {
      const numberValue = Number(value);

      if (!Number.isFinite(numberValue)) {
        return 0;
      }

      const factor = 10 ** decimals;

      return Math.trunc(numberValue * factor) / factor;
    },

    formatMTValue(value) {
      const truncatedValue = this.truncateToDecimals(value, 2);

      return truncatedValue.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatValue(value) {
      const numberValue = Number(value);

      if (!Number.isFinite(numberValue)) {
        return this.selectedMeasure === "mt" ? "0.00" : "0";
      }

      if (this.selectedMeasure === "mt") {
        return this.formatMTValue(numberValue);
      }

      return Math.round(numberValue).toLocaleString("en-US");
    },

    formatVariance(value) {
      const str = this.formatValue(Math.abs(value));
      return (value >= 0 ? "+" : "−") + str;
    },

    async fetchOrgStructList() {
      try {
        const res = await fetchNextOrgStructList(
          this.profileId,
          this.localToken,
        );
        this.orgStructures = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },

    handleRoleChange() {
      this.selectedName = null;
      this.selectedVehicle = "";
      this.vehicles = [];
      this.usersInOrgStructList = [];
      this.rawData = [];
      this.error = null;

      if (this.selectedRole) {
        this.fetchUsersInOrgStructure();
      } else {
        this.fetchData();
      }
    },

    async fetchUsersInOrgStructure() {
      try {
        const res = await fetchUserListByRole(
          this.profileId,
          this.selectedRole,
          this.localToken,
        );
        this.usersInOrgStructList = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },

    async loadItems() {
      try {
        const companyId = localStorage.getItem("company_id");
        const res = await fetchItemNameList(companyId, 1, this.localToken);
        if (res.data.status === "success") this.items = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },

    async fetchVehicles(distributorProfileId) {
      try {
        const res = await fetchDistributorVehicles(
          distributorProfileId,
          this.localToken,
        );
        if (res.data.success) this.vehicles = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },

    async fetchData() {
      if (this.selectedRole !== "" && this.selectedName === null) return;
      try {
        this.loading = true;
        this.error = null;
        this.rawData = [];

        const payload = {
          profile_id: this.profileId,
          distributor_id: this.selectedName || "",
          year: this.selectedYear,
          sku_id: this.selectedSku || "",
          vehicle_id: this.selectedVehicle || "",
        };

        const res = await fetchTargetsVsAchievements(payload, this.localToken);

        if (res.data.success === true && res.data.data.length > 0) {
          this.rawData = res.data.data;
        } else {
          this.error = res.data.message || "No data available";
        }
      } catch (e) {
        this.error = "Something went wrong. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    exportExcel() {
      const isMT = this.selectedMeasure === "mt";
      const measureLabel = isMT ? "MT" : "Qty";

      // ── 1. Resolve filter display values ──────────────────────────────────────
      const roleName =
        this.orgStructures.find((o) => o.id === this.selectedRole)
          ?.description || "All Roles";
      const userName =
        this.usersInOrgStructList.find(
          (u) => u.profile_id === this.selectedName,
        )?.name || "All Users";
      const skuName =
        this.items.find((i) => i.id === this.selectedSku)?.description ||
        "All SKUs";
      const monthName = this.selectedMonth
        ? MONTHS[this.selectedMonth - 1]
        : "All Year";

      // ── 2. Filter / metadata rows ──────────────────────────────────────────────
      const filterRows = [
        ["Target vs Achievement Report"],
        [],
        ["Role", roleName],
        ["User", userName],
        ["Year", this.selectedYear],
        ["Month", monthName],
        ["SKU", skuName],
        ["Measure", measureLabel],
        [],
      ];

      // ── 3. Column headers ──────────────────────────────────────────────────────
      const headerRow = [
        "#",
        this.selectedMonth ? "Date" : "Month",
        "Target (MT)",
        "Target (Qty)",
        "Achievement (MT)",
        "Achievement (Qty)",
        "Ach %",
        `Variance (${measureLabel})`,
      ];

      // ── 4. Data rows ───────────────────────────────────────────────────────────
      // const dataRows = this.tableRows.map((row, i) => [
      //   i + 1,
      //   row.label,
      //   parseFloat(row.target_mt.toFixed(4)),
      //   Math.round(row.target_qty),
      //   parseFloat(row.achieved_mt.toFixed(4)),
      //   Math.round(row.achieved_qty),
      //   row.pct.toFixed(2) + "%",
      //   (row.variance >= 0 ? "+" : "−") +
      //     this.formatValue(Math.abs(row.variance)),
      // ]);

      const dataRows = this.tableRows.map((row, i) => [
        i + 1,
        row.label,
        this.truncateToDecimals(row.target_mt, 2),
        Math.round(row.target_qty),
        this.truncateToDecimals(row.achieved_mt, 2),
        Math.round(row.achieved_qty),
        row.pct.toFixed(2) + "%",
        (row.variance >= 0 ? "+" : "−") +
          this.formatValue(Math.abs(row.variance)),
      ]);

      // ── 5. Totals row ─────────────────────────────────────────────────────────
      const s = this.summary;
      // const totalsRow = [
      //   "",
      //   "Total",
      //   parseFloat(s.target_mt.toFixed(4)),
      //   Math.round(s.target_qty),
      //   parseFloat(s.achieved_mt.toFixed(4)),
      //   Math.round(s.achieved_qty),
      //   s.pct.toFixed(2) + "%",
      //   (s.variance >= 0 ? "+" : "−") + this.formatValue(Math.abs(s.variance)),
      // ];

      const totalsRow = [
        "",
        "Total",
        this.truncateToDecimals(s.target_mt, 2),
        Math.round(s.target_qty),
        this.truncateToDecimals(s.achieved_mt, 2),
        Math.round(s.achieved_qty),
        s.pct.toFixed(2) + "%",
        (s.variance >= 0 ? "+" : "−") + this.formatValue(Math.abs(s.variance)),
      ];

      // ── 6. Footer ─────────────────────────────────────────────────────────────
      const firstName = localStorage.getItem("first_name") || "";
      const lastName = localStorage.getItem("last_name") || "";
      const userFullName = (firstName + " " + lastName).trim() || "-";
      const generatedAt = new Date().toLocaleString("en-US", { hour12: false });

      const footerRows = [
        [],
        ["Generated by", userFullName],
        ["Generated at", generatedAt],
      ];

      // ── 7. Assemble AOA ────────────────────────────────────────────────────────
      const totalCols = headerRow.length;
      const padTo = (arr) => {
        const out = Array.isArray(arr) ? [...arr] : [];
        while (out.length < totalCols) out.push("");
        return out.slice(0, totalCols);
      };

      const aoa = [
        ...filterRows.map(padTo),
        padTo(headerRow),
        ...dataRows.map(padTo),
        padTo(totalsRow),
        ...footerRows.map(padTo),
      ];

      // ── 8. Build worksheet ────────────────────────────────────────────────────
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const headerRowIndex = filterRows.length;

      // Merges
      const merges = [];
      // Title spans full width
      merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } });
      // Header cells
      ws["!merges"] = merges;

      // Column widths
      ws["!cols"] = [
        { wch: 5 }, // #
        { wch: 18 }, // Date/Month
        { wch: 14 }, // Target MT
        { wch: 13 }, // Target Qty
        { wch: 18 }, // Achievement MT
        { wch: 18 }, // Achievement Qty
        { wch: 10 }, // Ach %
        { wch: 18 }, // Variance
      ];

      // Style title (A1) bold + large
      const titleCell = XLSX.utils.encode_cell({ r: 0, c: 0 });
      if (ws[titleCell]) ws[titleCell].s = { font: { bold: true, sz: 14 } };

      // Style header row bold
      headerRow.forEach((_, ci) => {
        const cell = XLSX.utils.encode_cell({ r: headerRowIndex, c: ci });
        if (ws[cell]) ws[cell].s = { font: { bold: true } };
      });

      // Style totals row bold
      const totalsRowIndex = headerRowIndex + 1 + dataRows.length;
      headerRow.forEach((_, ci) => {
        const cell = XLSX.utils.encode_cell({ r: totalsRowIndex, c: ci });
        if (ws[cell]) ws[cell].s = { font: { bold: true } };
      });

      // ── 9. Write file ─────────────────────────────────────────────────────────
      const wb = XLSX.utils.book_new();
      const sheetName = "Target vs Achievement";
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      const fileName = `Target_vs_Achievement_${this.selectedYear}${
        this.selectedMonth ? `_${MONTHS[this.selectedMonth - 1]}` : ""
      }.xlsx`;
      XLSX.writeFile(wb, fileName);
    },
  },
};
</script>

<style>
.table tbody tr.extra-sale-day td {
  background-color: #fff8e1 !important;
}
</style>
